import { Box, Button, ButtonGroup, Flex, FormControl, FormLabel, IconButton, Input, Spinner } from "@chakra-ui/react";
import { useCallback, useState } from "react";
import { v4 as uuid } from "uuid";
import { useTranslation } from "react-i18next";
import { FaTrash } from "react-icons/fa";
import type {
  SourcingEventV2Dto,
  SourcingEventV2PatchDto,
} from "../../../../../autogen/bff-api";
import { useEditor } from "../../../../../common/editing/save-coordinator";

interface DescriptionEntry {
  id: string;
  value: string;
}

const toEntries = (descriptions: string[]): DescriptionEntry[] =>
  descriptions.map((value) => ({ id: uuid(), value }));

interface Props {
  eventId: string;
}

export const ContractOptionsField = ({ eventId }: Props) => {
  const { t } = useTranslation();
  const { state, updateField, fieldStatus } = useEditor<SourcingEventV2Dto, SourcingEventV2PatchDto>(eventId);

  const currentOptions = state.eventData.options;
  const hasOptions = currentOptions?.hasOptions ?? false;
  const [entries, setEntries] = useState<DescriptionEntry[]>(() =>
    currentOptions?.descriptions?.length ? toEntries(currentOptions.descriptions) : [{ id: uuid(), value: "" }],
  );

  const saving = fieldStatus("eventData.options");
  const isSaving = saving === "saving" || saving === "pending";

  const saveOptions = useCallback(
    (enabled: boolean, items: DescriptionEntry[], debounceMs = 0) => {
      const filtered = items.map((e) => e.value).filter((d) => d.trim() !== "");
      updateField({ eventData: { options: { hasOptions: enabled, descriptions: filtered } } }, { debounceMs });
    },
    [updateField],
  );

  const handleToggle = (enabled: boolean) => {
    if (!enabled) {
      setEntries([{ id: uuid(), value: "" }]);
      saveOptions(false, []);
    } else {
      saveOptions(true, entries);
    }
  };

  const handleDescriptionChange = (id: string, value: string) => {
    const updated = entries.map((e) => (e.id === id ? { ...e, value } : e));
    setEntries(updated);
    saveOptions(true, updated, 300);
  };

  const handleAdd = () => {
    setEntries([...entries, { id: uuid(), value: "" }]);
  };

  const handleRemove = (id: string) => {
    const updated = entries.filter((e) => e.id !== id);
    const result = updated.length === 0 ? [{ id: uuid(), value: "" }] : updated;
    setEntries(result);
    saveOptions(true, result);
  };

  return (
    <FormControl>
      <Flex align="center" justify="space-between" gap="4" mb={hasOptions ? "4" : "0"}>
        <FormLabel mb="0">{t("Contract options")}</FormLabel>
        <Flex align="center" gap="2">
          {isSaving && <Spinner size="xs" />}
          <ButtonGroup isAttached size="sm">
            <Button
              colorScheme={hasOptions ? "teal" : "gray"}
              variant={hasOptions ? "solid" : "outline"}
              onClick={() => handleToggle(true)}
            >
              {t("Yes")}
            </Button>
            <Button
              colorScheme={!hasOptions ? "teal" : "gray"}
              variant={!hasOptions ? "solid" : "outline"}
              onClick={() => handleToggle(false)}
            >
              {t("No")}
            </Button>
          </ButtonGroup>
        </Flex>
      </Flex>

      {hasOptions && (
        <Box>
          <Flex direction="column" gap="2">
            {entries.map((entry) => (
              <Flex key={entry.id} gap="2" align="center">
                <Input
                  value={entry.value}
                  placeholder={t("Describe the option") ?? undefined}
                  onChange={(e) => handleDescriptionChange(entry.id, e.target.value)}
                />
                <IconButton
                  aria-label={t("Remove")}
                  icon={<FaTrash />}
                  size="sm"
                  variant="ghost"
                  colorScheme="red"
                  onClick={() => handleRemove(entry.id)}
                />
              </Flex>
            ))}
          </Flex>
          <Button variant="ghost" colorScheme="teal" size="sm" mt="2" onClick={handleAdd}>
            + {t("Add option")}
          </Button>
        </Box>
      )}
    </FormControl>
  );
};
